import {
  API_OFFLINE,
  BASE_BACKOFF_MS,
  GET_DEADLINE_MS,
  MAX_RETRIES,
  withDeadline,
  RETRYABLE_ANY_METHOD,
  RETRYABLE_GET_ONLY,
  authErrorToApi,
  type ApiMethod,
  type ConditionalGetResult,
} from "@heraion/shared";

import { getIdToken } from "./auth/cognito";
import { isKnownDisconnected } from "./net";

const API_BASE = import.meta.env["VITE_API_URL"] ?? "";

export type ApiOptions = {
  method?: ApiMethod;
  body?: unknown;
  /** Skip the Authorization header (for public endpoints like /health). */
  anonymous?: boolean;
  /**
   * AN-6 — manda e ACCETTA i cookie su questa chiamata. Serve a una rotta sola,
   * `POST /api/animations/access`: senza, il browser scarta il Set-Cookie
   * firmato di CloudFront in silenzio e le animazioni vanno a 403 senza che
   * niente lo dica. ⛔ Resta opt-in per-chiamata invece di diventare il default:
   * ovunque altro l'autenticazione è il Bearer, e mandare cookie che nessuno
   * legge allargherebbe la superficie senza comprare niente.
   */
  withCookies?: boolean;
  /**
   * Extra request headers. Added for CO-7's optimistic lock, whose PATCH
   * REQUIRES `If-Match` (428 without it) — before this there was no way to send
   * one at all. Never clobbers the built-ins (Authorization / Content-Type /
   * Idempotency-Key): those are set after and win. Mirrors the mobile twin.
   */
  headers?: Record<string, string>;
  /**
   * Read the response `ETag` on success. Fires once, only on the returned
   * response (never on a retried attempt), with null when the header is absent.
   *
   * This is the WRITE VALIDATOR for the program-targets lock, not a body hash:
   * the caller round-trips it back as `If-Match`. ⛔ Do NOT feed it to
   * `If-None-Match` on /me/programs/:id — that route's ETag is
   * programEtag(updatedAt) and its body carries catalog-resolved `warnings`
   * that change with nobody writing, so a 304 there would hide real drift.
   */
  onEtag?: (etag: string | null) => void;
};

// Thin fetch wrapper. Attaches the Cognito ID token to Authorization
// (matches what the JWT authorizer expects — see infra/api.ts) and
// surfaces JSON errors as thrown ApiError. We don't auto-retry on 401:
// the SDK already refreshes silently while the refresh token is valid;
// a 401 here means the refresh token is gone, so the user must sign in.
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body: unknown,
  ) {
    super(message);
  }
}

// Retry policy + constants (RETRYABLE_*, MAX_RETRIES, BASE_BACKOFF_MS)
// lifted to @heraion/shared in 1.34.0 so the policy stays in sync
// with the mobile twin. Original WEB-6 rationale lives at the import
// site (packages/shared/src/apiRetry.ts).

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const newIdempotencyKey = (): string => {
  // crypto.randomUUID is in every modern target; fall back to a base36
  // composite if a future build path runs without it.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

// Bearer-auth headers shared by apiFetch + apiFetchConditional (mirrors
// apps/mobile/src/api/index.ts's authHeaders). Anonymous calls skip the
// token; a protected call with no token fails fast.
async function authHeaders(opts: {
  anonymous?: boolean;
}): Promise<Record<string, string>> {
  if (opts.anonymous) return {};
  // #397: offline, fail now, not after the token refresh (mobile's note).
  if (isKnownDisconnected()) throw new ApiError(0, API_OFFLINE, null);
  const token = await getIdToken().catch((err: unknown) => {
    throw authErrorToApi(err, (status, code) => new ApiError(status, code, null));
  });
  if (!token) throw new ApiError(401, "not_authenticated", null);
  return { Authorization: `Bearer ${token}` };
}

export async function apiFetch<T = unknown>(
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  const deadlineMethod = opts.method ?? "GET";
  // ⛔ Reads only — a save must never abandon itself on a clock.
  const ctrl = deadlineMethod === "GET" ? new AbortController() : null;
  const run = async (): Promise<T> => {
    // Caller headers first, built-ins after: a caller can add If-Match but can
    // never displace Authorization / Content-Type / Idempotency-Key.
    const headers: Record<string, string> = {
      ...opts.headers,
      ...(await authHeaders(opts)),
  };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  const method = opts.method ?? "GET";
  const isGet = method === "GET";
  // Stamp Idempotency-Key on every non-GET. The header is reused across
  // retries within this apiFetch call (so 429 retries dedup
  // server-side) and is the same key the user-triggered "retry" path
  // would carry implicitly — call sites that want strict
  // at-most-once-by-business-key can compose their own UUID by
  // calling apiFetch repeatedly with the same key in opts (not
  // supported yet; left as a future hook).
  if (!isGet) {
    headers["Idempotency-Key"] = newIdempotencyKey();
  }

  const url = `${API_BASE}${path}`;
  const init: RequestInit = {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: ctrl?.signal,
    ...(opts.withCookies ? { credentials: "include" as const } : {}),
  };

  const retryable = isGet ? RETRYABLE_GET_ONLY : RETRYABLE_ANY_METHOD;
  let attempt = 0;
  while (true) {
    const res = await fetch(url, init);
    if (res.ok) {
      opts.onEtag?.(res.headers.get("etag"));
      // 204 No Content (and the rare 205 Reset Content) carry no body.
      // Some upstreams still set Content-Type: application/json on the
      // response, which made us call res.json() on an empty stream and
      // throw SyntaxError. Treat any "no-body" status as a null payload
      // before touching the parser.
      if (res.status === 204 || res.status === 205) {
        return null as T;
      }
      const ct = res.headers.get("content-type") ?? "";
      const payload: unknown = ct.includes("application/json")
        ? await res.json()
        : await res.text();
      return payload as T;
    }
    if (retryable.has(res.status) && attempt < MAX_RETRIES) {
      const delay =
        BASE_BACKOFF_MS * Math.pow(2, attempt) + Math.random() * 100;
      attempt++;
      await sleep(delay);
      continue;
    }
    const ct = res.headers.get("content-type") ?? "";
    const payload: unknown = ct.includes("application/json")
      ? await res.json().catch(() => null)
      : await res.text().catch(() => "");
    throw new ApiError(res.status, `api_error_${res.status}`, payload);
  }
  };

  if (!ctrl) return run();
  // OP-35 — see apps/mobile/src/api/index.ts for the full reasoning: the
  // deadline wraps the token step too, because the hang sits upstream of
  // fetch(). withDeadline itself is shared (packages/shared/src/apiRetry.ts).
  return withDeadline(run(), () => ctrl.abort(), GET_DEADLINE_MS, (ms) =>
    // status 0: no server verdict was ever received.
    new ApiError(0, "api_timeout", { method: deadlineMethod, path, ms }),
  );
}

// Conditional GET with an optional If-None-Match validator. Mirrors
// apps/mobile/src/api/index.ts's apiFetchConditional. On 304 the server is
// telling us "the bytes you already hold are current" — the caller keeps its
// existing state untouched; on 200 it gets fresh data + the new ETag to
// persist for the next round. GET-only by construction (no body).
//
// ⛔ This is a DIFFERENT ETag family from `onEtag` above (the program-targets
// write-validator): only feed a route's own strong content-hash ETag back as
// `ifNoneMatch` on that SAME route, never across routes.
export async function apiFetchConditional<T = unknown>(
  path: string,
  opts: { ifNoneMatch?: string | null; anonymous?: boolean } = {},
): Promise<ConditionalGetResult<T>> {
  // OP-35 — same clock as the GET path above.
  const ctrl = new AbortController();
  const run = async (): Promise<ConditionalGetResult<T>> => {
    const headers: Record<string, string> = await authHeaders(opts);
    if (opts.ifNoneMatch) headers["If-None-Match"] = opts.ifNoneMatch;

    const url = `${API_BASE}${path}`;
    let attempt = 0;
    while (true) {
      const res = await fetch(url, {
        method: "GET",
        headers,
        signal: ctrl.signal,
      });
      if (res.status === 304) {
        return { status: 304, data: null, etag: res.headers.get("etag") };
      }
      if (res.ok) {
        const etag = res.headers.get("etag");
        if (res.status === 204 || res.status === 205) {
          return { status: 200, data: null as T, etag };
        }
        const ct = res.headers.get("content-type") ?? "";
        const payload: unknown = ct.includes("application/json")
          ? await res.json()
          : await res.text();
        return { status: 200, data: payload as T, etag };
      }
      if (RETRYABLE_GET_ONLY.has(res.status) && attempt < MAX_RETRIES) {
        const delay =
          BASE_BACKOFF_MS * Math.pow(2, attempt) + Math.random() * 100;
        attempt++;
        await sleep(delay);
        continue;
      }
      const ct = res.headers.get("content-type") ?? "";
      const payload: unknown = ct.includes("application/json")
        ? await res.json().catch(() => null)
        : await res.text().catch(() => "");
      throw new ApiError(res.status, `api_error_${res.status}`, payload);
    }
  };

  return withDeadline(run(), () => ctrl.abort(), GET_DEADLINE_MS, (ms) =>
    new ApiError(0, "api_timeout", { method: "GET", path, ms }),
  );
}
